import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import {
  Badge,
  Banner,
  Button,
  Empty,
  LayerCard,
  Loader,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { PrinterIcon } from '@phosphor-icons/react'
import type { PrintJob } from '@hub-wifi/shared'
import {
  getPublicPrinter,
  listMyPrintJobs,
  releasePrintJob,
  type PublicPrinter,
} from '../api/printJobs'
import { useTranslation } from '../i18n'
import { PrinterPicker } from '../components/print/PrinterPicker'
import { PrinterOfflineBanner } from '../components/print/PrinterOfflineBanner'
import { printerReachability } from '../lib/printerConnection'
import { describePrintError } from '../lib/printErrors'

/**
 * QR-landing release page. The QR sticker on the printer encodes
 * `https://hub.tesset.com/p/{printerId}`. The user lands here after scanning;
 * we show the printer name, online state, and the user's queued/failed jobs
 * with a big primary "Release here" button each.
 */
export function PrintReleasePage() {
  const { printerId } = useParams<{ printerId: string }>()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()
  const [printer, setPrinter] = useState<PublicPrinter | null>(null)
  const [jobs, setJobs] = useState<PrintJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [releasingId, setReleasingId] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  async function load() {
    if (!printerId) {
      // The route param is missing — surface a clean error instead of
      // leaving the page stuck on the loader forever.
      setError(t('print.release.printerNotFound'))
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const [p, items] = await Promise.all([
        getPublicPrinter({ token, printerId }),
        listMyPrintJobs(token),
      ])
      setPrinter(p)
      setJobs(items.filter((j) => j.status === 'queued' || j.status === 'failed'))
    } catch (err) {
      setError(describePrintError(err, t))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printerId])

  async function release(job: PrintJob, targetPrinterId: string, via: 'qr_scan' | 'manual_pick') {
    setReleasingId(job.id)
    try {
      const token = await getAccessTokenSilently()
      await releasePrintJob({ token, jobId: job.id, printerId: targetPrinterId, via })
      toasts.add({ variant: 'success', title: t('print.release.success') })
      await load()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: describePrintError(err, t),
      })
    } finally {
      setReleasingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <Loader size="lg" />
      </div>
    )
  }

  if (error || !printer) {
    return (
      <div className="mx-auto max-w-140 px-4 py-4">
        <Banner variant="error">{error ?? t('print.release.printerNotFound')}</Banner>
      </div>
    )
  }

  const reach = printerReachability(printer.epsonConnected, printer.connectionCheckedAt)

  return (
    <div className="mx-auto max-w-140 px-4 py-4">
      <LayerCard>
        <LayerCard.Primary className="p-4">
          <div className="flex items-center gap-3">
            <PrinterIcon size={32} />
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{printer.name}</div>
              <div className="text-sm text-kumo-subtle">{printer.buildingName}</div>
            </div>
            <Badge
              variant={reach === 'offline' ? 'error' : reach === 'online' ? 'success' : 'neutral'}
            >
              {reach === 'offline'
                ? t('print.picker.offline')
                : reach === 'online'
                  ? t('print.release.online')
                  : t('print.release.unknown')}
            </Badge>
          </div>
        </LayerCard.Primary>
      </LayerCard>

      <div className="mt-3">
        <PrinterOfflineBanner
          epsonConnected={printer.epsonConnected}
          connectionCheckedAt={printer.connectionCheckedAt}
          lastSeenConnectedAt={printer.lastSeenConnectedAt}
        />
      </div>

      <h2 className="mt-6 mb-2 text-base font-medium">{t('print.release.queueTitle')}</h2>
      {jobs.length === 0 ? (
        <Empty
          icon={<PrinterIcon size={40} className="text-kumo-inactive" />}
          title={t('print.release.empty')}
        />
      ) : (
        <ul className="space-y-3">
          {jobs.map((j) => (
            <li
              key={j.id}
              className="rounded-md border border-kumo-line p-3"
              data-testid={`release-job-${j.id}`}
            >
              <div className="truncate font-medium">{j.fileName}</div>
              <div className="mb-2 text-sm text-kumo-subtle">
                {new Date(j.uploadedAt).toLocaleString()}
              </div>
              <Button
                variant="primary"
                className="w-full"
                loading={releasingId === j.id}
                disabled={releasingId !== null}
                onClick={() => void release(j, printer.id, 'qr_scan')}
                type="button"
              >
                {t('print.release.releaseHere')}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 text-center text-sm">
        <span className="text-kumo-subtle">{t('print.release.wrongPrinter')}</span>{' '}
        <button
          type="button"
          className="text-kumo-link underline"
          onClick={() => setPickerOpen(true)}
        >
          {t('print.release.choosePrinter')}
        </button>
      </div>

      <PrinterPicker
        open={pickerOpen}
        buildingId={printer.buildingId}
        onPick={(otherPrinterId) => {
          setPickerOpen(false)
          // Use manual_pick when the user explicitly chose a different printer.
          if (jobs[0]) void release(jobs[0], otherPrinterId, 'manual_pick')
        }}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  )
}
